import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { HiOutlineChevronRight } from 'react-icons/hi2';
import { SectionCard } from '@themes/aegix';
import type { ProfileSite } from '../types';

export interface ProfileSitesSectionProps {
  sites: ProfileSite[];
  /**
   * Called when a site row is clicked. Only wired when `isSiteSelectable` is
   * true — AIM navigates to the per-site default-location view; SMS has no
   * default-location concept, so it leaves this off and rows are plain labels.
   */
  onSelectSite?: (site: ProfileSite) => void;
  /** Whether rows are clickable (shows a chevron + pointer). Default false. */
  isSiteSelectable?: boolean;
  title?: string;
  e2eTitle?: string;
}

/**
 * The user's sites. Structurally identical across apps; selectability degrades
 * gracefully: AIM rows navigate (chevron), SMS rows are static names.
 */
export function ProfileSitesSection({
  sites,
  onSelectSite,
  isSiteSelectable = false,
  title,
  e2eTitle,
}: ProfileSitesSectionProps) {
  const { t } = useTranslation();
  const selectable = isSiteSelectable && typeof onSelectSite === 'function';

  return (
    <SectionCard
      title={title ?? t('sites.title', t('common.sites', 'Sites'))}
      e2eTitle={e2eTitle}
    >
      <Box textAlign="start" maxH="25rem" overflowY="auto">
        {sites.map((site, index) => (
          <Flex
            key={site.id}
            pt={4}
            pb={3}
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid"
            borderColor="border.secondary"
            cursor={selectable ? 'pointer' : 'default'}
            onClick={selectable ? () => onSelectSite?.(site) : undefined}
            data-e2e={`site-${index}`}
          >
            <Text fontWeight={600}>{site.name}</Text>
            {selectable && <HiOutlineChevronRight />}
          </Flex>
        ))}
      </Box>
    </SectionCard>
  );
}

export default ProfileSitesSection;
